import { useState } from "react";
import { ApiError, PATIENT_NOTE_MAX_LENGTH, type PatientNote } from "@ferrivia/api-contract";
import { useApi, useTracker } from "@ferrivia/auth-web";
import { useFormatters, useTranslation } from "@ferrivia/i18n";
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  Text,
  Textarea,
} from "@ferrivia/ui";
import { useCustomerNotes } from "../../../../hooks/useCustomerNotes";
import s from "./NotesPane.module.css";

export interface NotesPaneProps {
  customerId: string;
}

// Below this many characters remaining the counter appears. Hidden the rest
// of the time: a live count on a box nobody is near filling reads as a length
// target rather than a limit.
const COUNTER_THRESHOLD = 200;

const SCOPE_ID = "notes-scope";

interface NoteEditorProps {
  customerId: string;
  notes: PatientNote[];
  onSaved: (note: PatientNote) => void;
}

function NoteEditor({ customerId, notes, onSaved }: NoteEditorProps) {
  const api = useApi();
  const tracker = useTracker();
  const { t } = useTranslation("clinic");
  const { dateTime } = useFormatters();

  // Mounted only once the notes have loaded, so the newest revision is known
  // at first render — no load-then-fill sequence to keep in step.
  const latest = notes[0];
  const [selectedId, setSelectedId] = useState(() => latest?.id ?? "");
  const [body, setBody] = useState(() => latest?.body ?? "");
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);

  const trimmed = body.trim();
  const remaining = PATIENT_NOTE_MAX_LENGTH - body.length;
  const tooLong = remaining < 0;
  // Compared against the newest revision rather than the selected one, which
  // is what makes restoring an older wording a single click: the field then
  // differs from the newest, so Save is live. Saving text identical to the
  // newest revision would only add a revision that says nothing.
  const duplicate = latest ? trimmed === latest.body.trim() : false;
  const canSave = trimmed.length > 0 && !tooLong && !duplicate && !saving;

  // Hoisted out of the revision label's interpolation below: i18next-parser
  // does not look inside another t() call's options object, so a nested call
  // there is a key the extractor silently drops from the catalog.
  const unknownAuthor = t("notesPane.unknownAuthor", "Ferrivia staff");

  function revisionLabel(note: PatientNote) {
    return t("notesPane.revision.entry", {
      when: dateTime.format(new Date(note.createdAt)),
      who: note.authorName ?? unknownAuthor,
      defaultValue: "{{when}} · {{who}}",
    });
  }

  function handleSelectRevision(id: string) {
    const picked = notes.find((note) => note.id === id);
    if (!picked) {
      return;
    }
    setSelectedId(id);
    setBody(picked.body);
    setFailed(false);
    tracker.track("clinic.patient_note_revision_viewed", {
      patient_id: customerId,
      revision_index: notes.indexOf(picked),
    });
  }

  async function handleSave() {
    if (!canSave) {
      return;
    }
    setSaving(true);
    setFailed(false);
    try {
      const note = await api.patients.addNote(customerId, { body: trimmed });
      onSaved(note);
      // The field keeps showing what was just written, now as the newest
      // revision. It is not cleared: this pane is one note plus its history,
      // not a compose box that empties after sending.
      setSelectedId(note.id);
      setBody(note.body);
    } catch (err) {
      setFailed(true);
      tracker.track("clinic.patient_note_add_failed", {
        patient_id: customerId,
        reason: err instanceof ApiError ? err.code : "network",
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={s.root}>
      {/* Every revision this note has ever had, newest first — the append-only
          record surfaced as the one control that reads it. Above the field
          because it chooses what the field holds. Picking one loads it in to
          edit or re-save; it never rewrites the revision itself, because
          nothing can (023_patient_notes.sql). Ordered by when each was
          written and by nothing else: no pinning, no grouping, nothing
          derived from what a revision says.

          Before the first save it renders inert and says so rather than
          disappearing: the field's history is a permanent part of this pane,
          and a control that appears out of nowhere on first save reads as a
          new feature rather than as the record it always was. */}
      {/* Controlled with "" rather than undefined before the first save: the
          empty string matches no item, which is what puts the trigger in its
          placeholder state, and it keeps the Select controlled for its whole
          lifetime instead of flipping when the first revision arrives. */}
      <Select
        value={selectedId}
        onValueChange={handleSelectRevision}
        disabled={saving || notes.length === 0}
      >
        <SelectTrigger
          className={s.revisions}
          aria-label={t("notesPane.revision.label", "Revision")}
          placeholder={t("notesPane.revision.none", "No revisions yet")}
        />
        <SelectContent>
          {notes.map((note) => (
            <SelectItem key={note.id} value={note.id}>
              {revisionLabel(note)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Textarea
        className={s.field}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        disabled={saving}
        invalid={tooLong}
        maxHeight={420}
        aria-label={t("notesPane.label", "Note")}
        aria-describedby={SCOPE_ID}
        placeholder={t("notesPane.placeholder", "What was agreed or arranged…")}
      />

      {/* One row under the field: the scope line, then the counter when it is
          near, then Save. The scope line is what the button sits against —
          given a row of its own it left Save alone at the far end of the next
          one, reading as a detached block rather than as this field's action.

          The line itself is the whole reason this pane can exist at stage-0,
          so it is permanent copy tied to the field via aria-describedby, not
          a dismissible hint: a note is a coordination record, and a
          clinician's assessment of this customer is a stage-1 capability
          (compliance/roadmap.md). */}
      <div className={s.actions}>
        <Text as="p" size="xs" id={SCOPE_ID} className={s.scope}>
          {t("notesPane.scope", "Operational notes only")}
        </Text>
        {remaining <= COUNTER_THRESHOLD && (
          <Text as="span" size="xs" className={tooLong ? s.counterOver : s.counter}>
            {t("notesPane.remaining", {
              count: remaining,
              defaultValue: "{{count}} characters left",
            })}
          </Text>
        )}
        <Button variant="primary" onClick={handleSave} disabled={!canSave}>
          {saving ? t("notesPane.saving", "Saving…") : t("notesPane.save", "Save note")}
        </Button>
      </div>

      {failed && (
        <Text as="p" size="sm" role="alert" className={s.error}>
          {t("notesPane.saveFailed", "Couldn't save the note. Your text is still here — try again.")}
        </Text>
      )}
    </div>
  );
}

/* The clinic console's Notes tab.
 *
 * Operational notes, deliberately and only: what was arranged with this
 * customer, who called whom, what they asked for. Not a clinician's
 * assessment of their health — that is the "record a clinician's assessment
 * against a named person" row in compliance/roadmap.md, a stage-1
 * capability that turns the platform into an asiakastietolaki
 * tietojärjestelmä and brings the whole regime at once. It needs the
 * palveluyksikkö registration and 27 § merkintä fields, not a text box, so
 * the boundary is stated under the field rather than left to whoever types
 * first.
 *
 * "The software does not interpret" binds this pane the same way it binds
 * Labs: every word here was typed by a named human being, and nothing on
 * the pane derives, scores, flags or orders a revision by anything other
 * than when it was written.
 *
 * Append-only, all the way down (023_patient_notes.sql): no edit control, no
 * delete control, and no revision the pane can overwrite — saving always adds
 * one. The dropdown is the record made visible, and it is why the pane can
 * read as a single note field without pretending the earlier wordings never
 * existed.
 */
export function NotesPane({ customerId }: NotesPaneProps) {
  const { t } = useTranslation("clinic");
  const [state, prependNote] = useCustomerNotes(customerId);

  // No browser-side "notes viewed" event here, unlike DetailsPane: the pane
  // cannot render without GET /clinic/patients/:id/notes, and that route
  // already emits clinic.patient_notes_viewed with the count. A second
  // client-side copy of the same fact would only be a way for the two to
  // disagree.
  if (state.status === "loading") {
    return (
      <Text as="p" size="sm" className={s.helper}>
        {t("notesPane.loading", "Loading notes…")}
      </Text>
    );
  }

  if (state.status === "error") {
    return (
      <Text as="p" size="sm" className={s.helper}>
        {t("notesPane.error", "Couldn't load notes.")}
      </Text>
    );
  }

  return <NoteEditor customerId={customerId} notes={state.notes} onSaved={prependNote} />;
}
